import 'swiper/css'
import 'swiper/css/pagination'

import clsx from 'clsx'
import type dayjs from 'dayjs'
import { Navigation, Pagination, Scrollbar, A11y } from 'swiper/modules'
import type { SwiperModule, Swiper as SwiperType } from 'swiper/types'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { computed, defineComponent, onMounted, ref } from 'vue'

import SelectDate from '@/components/common/date/SelectDate'
import VIcon from '@/components/common/VIcon'
import VTabs from '@/components/common/VTabs'
import useAuth from '@/composables/auth/useAuth'
import useBreakpoints from '@/composables/common/useBreakpoints'
import useLocalization from '@/composables/localization/useLocalization'
import useBookingView from '@/composables/views/booking/useBookingView'
import { getLocalizationName } from '@/utils/localization'

import AdmissionRoomItem from './AdmissionRoomItem'

export default defineComponent(() => {
  const auth = useAuth()
  const { isMobile } = useBreakpoints()
  const { localeTexts, localizationNameKey, translationWithFragmentKeys } = useLocalization()
  const swiper = ref<SwiperType>()
  const swiperModules = ref<SwiperModule[]>([Navigation, Pagination, Scrollbar, A11y])
  const {
    isRequireRoomForAdmission,
    admissionRoomSelected,
    admissionRoomStartDate,
    admissionRoomEndDate,
    admissionRoomSelectedIndex,
    allAdmissionRoomsQuery,
    setAdmissionRoomStartDate,
    setAdmissionRoomEndDate
  } = useBookingView()

  const admissionRoomsTabIndex = ref(admissionRoomSelectedIndex.value)

  // #region Getters

  const maxAdmissionRoomStartDate = computed((): dayjs.Dayjs | null => {
    if (admissionRoomEndDate.value === null) {
      return null
    }

    return admissionRoomEndDate.value.add(-1, 'day')
  })

  const minAdmissionRoomEndDate = computed((): dayjs.Dayjs | null => {
    if (admissionRoomStartDate.value === null) {
      return null
    }

    return admissionRoomStartDate.value.add(1, 'day')
  })

  const admissionRoomSelectedName = computed((): string => {
    if (admissionRoomSelected.value === null) {
      return '-'
    }

    const name = getLocalizationName(admissionRoomSelected.value, auth.userProfile!.language_preference)

    return name ?? '-'
  })

  // #endregion

  onMounted(() => {
    swiper.value?.slideTo(admissionRoomsTabIndex.value)
  })

  return () => (
    <div class='rounded border border-neutral-300 p-4'>
      <div class='flex flex-col gap-x-6 gap-y-8 tablet:flex-row'>
        <SelectDate
          t-data='booking-admission-room-start-date-select-date'
          class='flex-1'
          label={localeTexts.value['create-booking-step1-room-start-label']}
          required={isRequireRoomForAdmission.value}
          variant='floating'
          value={admissionRoomStartDate.value}
          max={maxAdmissionRoomStartDate.value}
          isMobileMode={isMobile.value}
          onChange={setAdmissionRoomStartDate}
        />
        <SelectDate
          t-data='booking-admission-room-end-date-select-date'
          class='flex-1'
          label={localeTexts.value['create-booking-step1-room-end-label']}
          required={isRequireRoomForAdmission.value}
          variant='floating'
          value={admissionRoomEndDate.value}
          min={minAdmissionRoomEndDate.value}
          isMobileMode={isMobile.value}
          onChange={setAdmissionRoomEndDate}
        />
      </div>
      <div class='mt-6 hidden tablet:block'>
        <VTabs
          t-data='booking-admission-room-pagination-tabs'
          value={admissionRoomsTabIndex.value}
          items={allAdmissionRoomsQuery.data.value}
          itemKey='id'
          itemText={localizationNameKey.value}
          onChange={(index) => {
            admissionRoomsTabIndex.value = index
            swiper.value?.slideTo(index)
          }}
        />
      </div>
      <Swiper
        modules={swiperModules.value}
        class='mt-4'
        slidesPerView='auto'
        onSlideChange={(value) => {
          admissionRoomsTabIndex.value = value.activeIndex
        }}
        onSwiper={(value) => {
          swiper.value = value
        }}
      >
        {allAdmissionRoomsQuery.data.value?.map((admissionRoom) => {
          return (
            <SwiperSlide
              key={`booking-admission-room-${admissionRoom.id}`}
              class='!h-auto desktop:!h-[240px]'
            >
              <AdmissionRoomItem item={admissionRoom} />
            </SwiperSlide>
          )
        })}
      </Swiper>
      <div class='mt-4 flex justify-center tablet:hidden'>
        <div class='flex items-center'>
          {allAdmissionRoomsQuery.data.value?.map((admissionRoom, index) => {
            const isSelect = admissionRoomsTabIndex.value === index
            return (
              <button
                key={`booking-admission-room-pagination-${admissionRoom.id}`}
                class={clsx('flex aspect-square size-3 flex-none items-center justify-center rounded-full bg-white', { '!bg-primary-50': isSelect })}
                type='button'
                onClick={() => {
                  admissionRoomsTabIndex.value = index
                  swiper.value?.slideTo(index)
                }}
              >
                <VIcon
                  class={clsx('text-[8px] text-neutral-300', { '!text-primary': isSelect })}
                  icon='material-symbols:circle'
                />
              </button>
            )
          })}
        </div>
      </div>
      {admissionRoomSelected.value &&
        <div class='mt-4 flex items-center space-x-2 tablet:mt-6'>
          <div class='text-subtitle-1 text-neutral-500'>{translationWithFragmentKeys('create-booking-step1-room-selected-text', [{ fragment_key: 'room', value: '' }])}</div>
          <div class='text-body-1 text-neutral-500'>{admissionRoomSelectedName.value}</div>
        </div>}
    </div>
  )
}, {
  name: 'AdmissionRoomForm'
})
